export const DEFAULT_HUB_DISPLAY_NAME = "Lifter Hub";
export const DEFAULT_BRAND_ACCENT = "#d4781a";
// White and near-black ink (the latter matches --color-text) are the only two
// on-accent label colors.
const ON_ACCENT_WHITE = "#ffffff";
const ON_ACCENT_INK = "#1a1a1a";
export const DEFAULT_BRAND_ACCENT_CONTRAST = ON_ACCENT_WHITE;
export const DEFAULT_TYPEFACE_ID: TypefaceId = "jakarta";

export const ACCENT_CONTRAST_WARNING_MESSAGE =
  "This accent is below the 3:1 contrast guideline against white — buttons, links, and the focus ring may be hard to see. Consider a darker shade.";
export const ACCENT_LABEL_CONTRAST_WARNING_MESSAGE =
  "No label color clears the 4.5:1 text-contrast guideline on this accent, so button and link text may be hard to read. Try a lighter or darker shade.";
export const ACCENT_INVALID_HEX_MESSAGE = "Accent color must be a 6-digit hex value, like #2563eb.";
export const TYPEFACE_INVALID_MESSAGE = "Typeface must be one of the whitelisted options.";

// biome-ignore lint/suspicious/noControlCharactersInRegex: detecting control chars is the point
const DISPLAY_NAME_CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
// WCAG 1.4.11 (non-text contrast) is the applicable rule: the accent drives UI
// components (primary buttons, active nav, the focus ring), which require 3:1
// against their background. The 4.5:1 ratio governs body text, and the accent
// is never rendered as body text. Text placed on the accent is guaranteed
// separately by the computed `--brand-contrast`, which flips to dark ink on
// light accents.
//
// ADVISORY, not a gate (operator decision 2026-07-20): tenants must be able to
// set their exact brand color, so a below-threshold accent saves and the form
// surfaces `ACCENT_CONTRAST_WARNING_MESSAGE` instead of rejecting.
const WCAG_AA_NONTEXT_CONTRAST = 3.0;
const WCAG_AA_TEXT_CONTRAST = 4.5;

export type TypefaceDisplayPair = "sans" | "serif";

export interface TypefaceWhitelistEntry {
  id: TypefaceId;
  displayName: string;
  bodyFamily: string;
  displayFamily: string;
  pair: TypefaceDisplayPair;
  googleFontsName: string;
  googleFontsAxis?: string;
}

const SANS_FALLBACK = `-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;

const TYPEFACE_WHITELIST = [
  {
    id: "jakarta",
    displayName: "Plus Jakarta Sans",
    bodyFamily: `"Plus Jakarta Sans", ${SANS_FALLBACK}`,
    displayFamily: `"Plus Jakarta Sans", ${SANS_FALLBACK}`,
    pair: "sans",
    googleFontsName: "Plus Jakarta Sans",
    googleFontsAxis: "wght@300;400;500;600;700;800",
  },
  {
    id: "instrument-sans",
    displayName: "Instrument Sans",
    bodyFamily: `"Instrument Sans", ${SANS_FALLBACK}`,
    displayFamily: `"Instrument Sans", ${SANS_FALLBACK}`,
    pair: "sans",
    googleFontsName: "Instrument Sans",
    googleFontsAxis: "wght@400;500;600;700",
  },
  {
    id: "dm-sans",
    displayName: "DM Sans",
    bodyFamily: `"DM Sans", ${SANS_FALLBACK}`,
    displayFamily: `"DM Sans", ${SANS_FALLBACK}`,
    pair: "sans",
    googleFontsName: "DM Sans",
    googleFontsAxis: "opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700",
  },
  {
    id: "manrope",
    displayName: "Manrope",
    bodyFamily: `"Manrope", ${SANS_FALLBACK}`,
    displayFamily: `"Manrope", ${SANS_FALLBACK}`,
    pair: "sans",
    googleFontsName: "Manrope",
    googleFontsAxis: "wght@400;500;600;700;800",
  },
  {
    id: "fraunces",
    displayName: "Fraunces (editorial pair)",
    bodyFamily: `"Plus Jakarta Sans", ${SANS_FALLBACK}`,
    displayFamily: `"Fraunces", Georgia, serif`,
    pair: "serif",
    googleFontsName: "Fraunces",
    googleFontsAxis: "opsz,wght,SOFT@9..144,400;9..144,500;9..144,600;9..144,700",
  },
  {
    id: "instrument-serif",
    displayName: "Instrument Serif (minimal pair)",
    bodyFamily: `"Plus Jakarta Sans", ${SANS_FALLBACK}`,
    displayFamily: `"Instrument Serif", Georgia, serif`,
    pair: "serif",
    googleFontsName: "Instrument Serif",
    googleFontsAxis: "ital@0;1",
  },
] as const;

export type TypefaceId = (typeof TYPEFACE_WHITELIST)[number]["id"];

export const TYPEFACE_WHITELIST_ENTRIES: readonly TypefaceWhitelistEntry[] = TYPEFACE_WHITELIST;

const TYPEFACE_INDEX: Record<TypefaceId, TypefaceWhitelistEntry> = TYPEFACE_WHITELIST.reduce(
  (acc, entry) => {
    acc[entry.id] = entry;
    return acc;
  },
  {} as Record<TypefaceId, TypefaceWhitelistEntry>,
);

export function isTypefaceId(value: unknown): value is TypefaceId {
  return typeof value === "string" && value in TYPEFACE_INDEX;
}

export function resolveTypeface(value: unknown): TypefaceWhitelistEntry {
  if (isTypefaceId(value)) return TYPEFACE_INDEX[value];
  return TYPEFACE_INDEX[DEFAULT_TYPEFACE_ID];
}

export interface BrandingFormValues {
  displayName: string;
  accentColor: string;
  typefaceId: string;
}

export interface BrandingFormErrors {
  displayName?: string;
  accentColor?: string;
  typefaceId?: string;
}

export interface BrandingFormPayload {
  displayName: string | null;
  accentColor: string | null;
  typefaceId: TypefaceId | null;
}

export interface BrandingSourceOrg {
  displayName: string | null;
  accentColor?: string | null;
  typefaceId?: string | null;
}

export interface AccentResolutionInput {
  accentColor?: string | null;
}

export function resolveBrandingDisplayName(org: { displayName: string | null }): string {
  const trimmed = (org.displayName ?? "").trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_HUB_DISPLAY_NAME;
}

/**
 * The browser tab: what this page is, then whose product it belongs to. The
 * tenant's own name goes where the vendor's used to, so a white-labeled
 * instance never announces Lifter in the most visible chrome the browser has.
 * An unbranded instance keeps the deployment default.
 */
export function documentTitle(page: string, org: { displayName: string | null }): string {
  const site = resolveBrandingDisplayName(org);
  const trimmed = page.trim();
  if (!trimmed || trimmed === site) return site;
  return `${trimmed} · ${site}`;
}

export function brandingValuesFromOrg(org: BrandingSourceOrg): BrandingFormValues {
  return {
    displayName: org.displayName ?? "",
    accentColor: org.accentColor ?? "",
    typefaceId: isTypefaceId(org.typefaceId) ? org.typefaceId : "",
  };
}

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR_PATTERN.test(value);
}

export function relativeLuminance(hex: string): number {
  const channel = (raw: number): number => {
    const c = raw / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const value = hex.replace(/^#/, "");
  const r = channel(Number.parseInt(value.slice(0, 2), 16));
  const g = channel(Number.parseInt(value.slice(2, 4), 16));
  const b = channel(Number.parseInt(value.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG relative-contrast ratio between two #RRGGBB colors (1:1 to 21:1). */
export function contrastRatio(hexA: string, hexB: string): number {
  const lA = relativeLuminance(hexA);
  const lB = relativeLuminance(hexB);
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

export function resolveAccentColor(input: AccentResolutionInput): string {
  if (!isHexColor(input.accentColor)) return DEFAULT_BRAND_ACCENT;
  return input.accentColor.toLowerCase();
}

/**
 * On-accent label color. White is the default: a solid accent carrying dark ink
 * reads as a disabled or warning surface rather than as the brand, and the
 * saturated mid-tones most tenants pick (including the default amber #d4781a)
 * are exactly the range where a raw ratio comparison would choose ink.
 *
 * Ink is therefore the exception, taken only when white is genuinely illegible —
 * white below the 3:1 non-text bar, which is the same threshold the accent
 * itself is advised against. Picking purely by the higher ratio is deliberately
 * not the rule: it maximises a number at the cost of the brand reading wrong on
 * every primary button.
 */
export function resolveAccentContrast(hex: string): string {
  if (!isHexColor(hex)) return DEFAULT_BRAND_ACCENT_CONTRAST;
  return contrastRatio(hex, ON_ACCENT_WHITE) >= WCAG_AA_NONTEXT_CONTRAST
    ? ON_ACCENT_WHITE
    : ON_ACCENT_INK;
}

export function contrastRatioVsWhite(hex: string): number {
  const luminance = relativeLuminance(hex);
  return 1.05 / (luminance + 0.05);
}

export function accentMeetsContrast(hex: string): boolean {
  if (!isHexColor(hex)) return false;
  return contrastRatioVsWhite(hex) >= WCAG_AA_NONTEXT_CONTRAST;
}

/**
 * Best *achievable* on-accent label contrast — the higher of white-vs-accent and
 * ink-vs-accent, which is not necessarily the ratio of the color
 * `resolveAccentContrast` actually picks. The advisory this feeds is a statement
 * about the accent ("no label color clears 4.5:1 on this shade"), so it must
 * stay achievability: measuring the chosen color instead would warn on every
 * mid-tone brand, the default amber included.
 */
export function accentLabelContrast(hex: string): number {
  return Math.max(contrastRatio(hex, ON_ACCENT_WHITE), contrastRatio(hex, ON_ACCENT_INK));
}

export function accentLabelMeetsContrast(hex: string): boolean {
  if (!isHexColor(hex)) return false;
  return accentLabelContrast(hex) >= WCAG_AA_TEXT_CONTRAST;
}

export interface AccentValidationResult {
  valid: boolean;
  error?: string;
}

export function validateAccentColor(value: string | null | undefined): AccentValidationResult {
  if (value === null || value === undefined || value === "") return { valid: true };
  if (!isHexColor(value)) return { valid: false, error: ACCENT_INVALID_HEX_MESSAGE };
  return { valid: true };
}

/**
 * Advisory contrast note for a syntactically valid accent. Returns the non-text
 * warning when the accent itself is below the 3:1 UI-component bar, otherwise
 * the label warning when no on-accent label color clears the 4.5:1 text bar.
 * `undefined` when the value is empty, malformed (the format error owns that),
 * or clears both bars. Never blocks a save; the form renders this as a warning
 * beside the field.
 */
export function accentContrastWarning(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  if (!isHexColor(value)) return undefined;
  if (!accentMeetsContrast(value)) return ACCENT_CONTRAST_WARNING_MESSAGE;
  if (!accentLabelMeetsContrast(value)) return ACCENT_LABEL_CONTRAST_WARNING_MESSAGE;
  return undefined;
}

export function validateTypefaceId(value: string | null | undefined): AccentValidationResult {
  if (value === null || value === undefined || value === "") return { valid: true };
  if (!isTypefaceId(value)) return { valid: false, error: TYPEFACE_INVALID_MESSAGE };
  return { valid: true };
}

export function validateBrandingForm(values: BrandingFormValues): {
  valid: boolean;
  errors: BrandingFormErrors;
} {
  const errors: BrandingFormErrors = {};
  const displayName = values.displayName.trim();
  if (displayName.length > 50) {
    errors.displayName = "Display name must be 50 characters or fewer";
  } else if (displayName.length > 0 && DISPLAY_NAME_CONTROL_CHARS.test(displayName)) {
    errors.displayName = "Display name must not contain control characters";
  }

  const accentColor = values.accentColor.trim();
  if (accentColor.length > 0) {
    const accentResult = validateAccentColor(accentColor);
    if (!accentResult.valid && accentResult.error) {
      errors.accentColor = accentResult.error;
    }
  }

  const typefaceId = values.typefaceId.trim();
  if (typefaceId.length > 0 && !isTypefaceId(typefaceId)) {
    errors.typefaceId = TYPEFACE_INVALID_MESSAGE;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export function brandingFormToPayload(values: BrandingFormValues): BrandingFormPayload {
  const displayName = values.displayName.trim();
  const accentColor = values.accentColor.trim();
  const typefaceId = values.typefaceId.trim();
  return {
    displayName: displayName.length > 0 ? displayName : null,
    accentColor: accentColor.length > 0 ? accentColor.toLowerCase() : null,
    typefaceId: isTypefaceId(typefaceId) ? typefaceId : null,
  };
}
